import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Users,
  Award,
  BarChart3,
  TrendingUp,
  CalendarDays,
  ListChecks,
  Settings as SettingsIcon,
  Loader2,
  Sparkles,
  PieChart,
  Trophy,
  DoorOpen,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import JoinCommunityModal from '@/components/JoinCommunityModal';
import CommunityWatchlistsTab from '@/components/CommunityWatchlistsTab';
import CommunityCalendarTab from '@/components/CommunityCalendarTab';
import CommunitySettingsTab from '@/components/CommunitySettingsTab';
import CommunityFeedTab from '@/components/CommunityFeedTab';
import LeaderboardTab from '@/components/LeaderboardTab';
import TournamentTab from '@/components/TournamentTab';
import WelcomeBackModal from '@/components/WelcomeBackModal';
import CopyWatchlistButton from '@/components/CopyWatchlistButton';
import TrendingWatchlistsRibbon from '@/components/TrendingWatchlistsRibbon';
import { formatMoney } from '@/lib/money';
import { shouldAutoRefresh } from '@/lib/marketHoursPolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_DEFS = [
  { key: 'feed',        label: 'Feed',        Icon: TrendingUp },
  { key: 'leaderboard', label: 'Leaderboard', Icon: Trophy },
  { key: 'tournament',  label: 'Tournament',  Icon: Trophy },
  { key: 'hub',         label: 'Hub',         Icon: BarChart3 },
  { key: 'calendar',    label: 'Calendar',    Icon: CalendarDays },
  { key: 'watchlists',  label: 'Watchlists',  Icon: ListChecks },
  { key: 'settings',    label: 'Settings',    Icon: SettingsIcon },
];

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'feed';
  const [me, setMe] = useState(null);
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const meRes = await axios.get(`${API}/community/me`, { withCredentials: true });
        if (cancelled) return;
        setMe(meRes.data);
        if (meRes.data.is_member) {
          const hubRes = await axios.get(`${API}/community/hub`, { withCredentials: true });
          if (cancelled) return;
          setHub(hubRes.data);
        }
      } catch {
        if (!cancelled) setMe({ is_member: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, tab]);

  // Round 84k Phase 3.1 — extend 5-minute auto-refresh from the
  // leaderboard to the Hub stats so the Community Avg Yield / Quality
  // numbers stay live without forcing a page reload. Silently
  // re-fetches /community/hub when the user is on the hub tab.
  //
  // Round 84k Phase 3.2 — gated to production hostname AND 9:30 AM →
  // 4:15 PM ET market window so the preview environment never polls.
  useEffect(() => {
    if (!me?.is_member) return undefined;
    if (tab !== 'hub') return undefined;
    if (!shouldAutoRefresh()) return undefined;
    const id = setInterval(async () => {
      try {
        const hubRes = await axios.get(`${API}/community/hub`, { withCredentials: true });
        setHub(hubRes.data);
      } catch { /* silent — keeps last good hub data */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [tab, me?.is_member]);

  const setTab = (k) => setSearchParams({ tab: k });

  // Logged-out OR not a member — show the join gate
  if (!loading && (!user || !me?.is_member)) {
    return (
      <div className="min-h-screen min-h-[100svh] bg-background" data-testid="community-page-gate">
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
          <div className="mb-3">
            <BackOrHomeButton testid="community-gate-back-or-home-btn" />
          </div>
          <div className="rounded-2xl border-2 border-violet-300 dark:border-violet-700 bg-gradient-to-br from-violet-50 via-background to-background dark:from-violet-950/40 dark:via-background dark:to-background p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold">DiviDrip Community</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {!user
                    ? 'You need to be signed in to access the community. Use your DiviDrip account or sign in with Google.'
                    : 'Compare your dividend strategy with verified investors. Privacy-first — dollars stay yours.'}
                </p>
              </div>
            </div>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-start gap-2"><span className="text-violet-500 dark:text-violet-400 mt-0.5">✓</span> Anonymous sector + yield benchmarks vs the whole community</li>
              <li className="flex items-start gap-2"><span className="text-violet-500 dark:text-violet-400 mt-0.5">✓</span> Earn 8 passive-income milestone badges</li>
              <li className="flex items-start gap-2"><span className="text-violet-500 dark:text-violet-400 mt-0.5">✓</span> Publish up to 3 watchlists × 5 tickers — discover what others hold</li>
              <li className="flex items-start gap-2"><span className="text-violet-500 dark:text-violet-400 mt-0.5">✓</span> Community sentiment polls on the most-held tickers</li>
              <li className="flex items-start gap-2"><span className="text-violet-500 dark:text-violet-400 mt-0.5">✓</span> Diversification leaderboard — never ranked by yield chasing</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              {user ? (
                <Button
                  onClick={() => setJoinOpen(true)}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="community-gate-join-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1" /> Join the Community
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/')}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="community-gate-login-btn"
                >
                  Sign in to join
                </Button>
              )}
              <Link to="/community/terms" className="inline-flex">
                <Button variant="outline" data-testid="community-gate-terms-btn">Read the Community Terms</Button>
              </Link>
            </div>
          </div>
          {/* mt-3 (12px) brings the visible border-to-border gap
              between the violet Community card and the red disclaimer
              up to ~27px — matching the airy ~25px gap the main page
              has between the pagination text and the disclaimer. */}
          <SiteDisclaimer />
        </main>
        <JoinCommunityModal
          open={joinOpen}
          onClose={() => setJoinOpen(false)}
          onJoined={() => { setJoinOpen(false); /* effect refetches */ }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="community-page">
      <WelcomeBackModal />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <BackOrHomeButton testid="community-page-back-or-home-btn" showEscHint={false} />
            <div className="w-10 h-10 rounded-2xl bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">DiviDrip Community</h1>
              <p className="text-xs text-muted-foreground">
                @{me?.handle} · Joined {me?.joined_at ? new Date(me.joined_at).toLocaleDateString() : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-4 overflow-x-auto">
          <div className="flex gap-1 sm:gap-2 min-w-max">
            {TAB_DEFS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === key
                    ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`community-tab-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading community…
          </div>
        )}

        {!loading && tab === 'feed' && <CommunityFeedTab me={me} />}
        {!loading && tab === 'leaderboard' && <LeaderboardTab />}
        {!loading && tab === 'tournament' && <TournamentTab myHandle={me?.handle} />}
        {!loading && tab === 'hub' && <HubTab hub={hub} me={me} />}
        {!loading && tab === 'calendar' && <CommunityCalendarTab />}
        {!loading && tab === 'watchlists' && <CommunityWatchlistsTab me={me} />}
        {!loading && tab === 'settings' && <CommunitySettingsTab me={me} onChange={setMe} />}

        {/* mt-3 to match the main page's ~25px airy gap — see the
            logged-out gate above for the rationale. */}
        <SiteDisclaimer />
      </main>
    </div>
  );
}

function HubTab({ hub, me }) {
  const bench = hub?.benchmarks || {};
  const you = hub?.you || null;
  const polls = hub?.polls || [];
  const topWatchlists = hub?.top_watchlists || [];

  const sectors = useMemo(() => {
    const w = bench.sector_weights || {};
    const arr = Object.entries(w).map(([s, p]) => ({ sector: s, pct: p }));
    arr.sort((a, b) => b.pct - a.pct);
    return arr.slice(0, 6);
  }, [bench.sector_weights]);

  return (
    <div className="space-y-5">
      {/* Trending watchlists — Round 84b social-proof ribbon. Self-hides
          when no recent copies exist anywhere, so the slot stays
          invisible until the mechanic gains traction organically. */}
      <TrendingWatchlistsRibbon myHandle={me?.handle} />

      {/* Top stat cards — Round 83: Members count removed (admin-only).
          See /admin/community for total members + members online. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard
          label="Community Avg Yield"
          value={bench.avg_yield ? `${Number(bench.avg_yield).toFixed(2)}%` : '—'}
          icon={TrendingUp}
          tone="emerald"
        />
        <StatCard
          label="Avg Quality Score"
          value={bench.avg_quality_score || '—'}
          icon={Sparkles}
          tone="amber"
        />
      </div>

      {/* You section */}
      {you && (
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="community-you-section">
          <h2 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-violet-500 dark:text-violet-400" />
            Your DiviDrip Stats
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Projected Annual Income</div>
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{formatMoney(you.annual_income ?? 0)}</div>
              <div className="text-[11px] text-muted-foreground">≈ {formatMoney(you.monthly_average ?? 0)}/month</div>
            </div>
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Badges Earned</div>
              <div className="text-xl font-bold mt-0.5">{you.badges?.length || 0} / 8</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(you.badges || []).map((b) => (
                  <span
                    key={b.key}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300"
                    title={b.blurb}
                  >
                    {b.emoji} {b.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Next Milestone</div>
              {you.next_milestone ? (
                <>
                  <div className="text-sm font-bold mt-0.5">{you.next_milestone.emoji} {you.next_milestone.label}</div>
                  <div className="text-[11px] text-muted-foreground">{formatMoney(you.next_milestone.remaining)}/yr to go</div>
                </>
              ) : (
                <div className="text-sm font-bold mt-0.5 text-emerald-600 dark:text-emerald-400">🎉 Maxed out — Lifestyle Fund unlocked!</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sector benchmark + Top watchlists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="community-sectors-section">
          <h2 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-violet-500 dark:text-violet-400" />
            Community Sector Weights
          </h2>
          {sectors.length === 0 ? (
            <p className="text-sm text-muted-foreground">No data yet — once a few members opt in to anonymous benchmarks, sector weights will appear here.</p>
          ) : (
            <div className="space-y-2">
              {sectors.map((s) => (
                <div key={s.sector} className="flex items-center gap-2">
                  <div className="w-32 sm:w-40 text-xs text-foreground/80 truncate">{s.sector}</div>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 dark:bg-violet-400 rounded-full"
                      style={{ width: `${Math.min(100, s.pct)}%` }}
                    />
                  </div>
                  <div className="w-12 text-right text-xs font-mono">{s.pct.toFixed(1)}%</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="community-top-watchlists-section">
          <h2 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-violet-500 dark:text-violet-400" />
            Top Public Watchlists
          </h2>
          {topWatchlists.length === 0 ? (
            <p className="text-sm text-muted-foreground">No published watchlists yet. Be the first to publish one from the Watchlists tab.</p>
          ) : (
            <ul className="space-y-2">
              {topWatchlists.map((w) => (
                <li key={w.watchlist_id} className="rounded-lg border border-border bg-background p-2 text-sm" data-testid={`public-watchlist-row-${w.watchlist_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate flex-1 min-w-0">{w.name}</span>
                    <span className="text-[10px] text-muted-foreground">by @{w.handle}</span>
                    {me?.handle && me.handle !== w.handle ? (
                      <CopyWatchlistButton watchlistId={w.watchlist_id} />
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {/* If the backend returned the new `items` shape,
                        render each ticker with a "✓N" social-proof
                        chip next to it. Falls back to the legacy
                        flat list for older payloads. */}
                    {(w.items || (w.tickers || []).map((t) => ({ ticker: t, verified_owner_count: 0 }))).map((it) => (
                      <span
                        key={it.ticker}
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-muted"
                        title={it.verified_owner_count > 0
                          ? `${it.verified_owner_count} verified owner${it.verified_owner_count === 1 ? '' : 's'} in the community`
                          : 'No verified owners yet'}
                      >
                        <span className="font-mono">{it.ticker}</span>
                        {it.verified_owner_count > 0 && (
                          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                            ✓{it.verified_owner_count}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  {w.copy_count > 0 ? (
                    <p className="text-[10px] text-muted-foreground mt-1">📋 Copied {w.copy_count} {w.copy_count === 1 ? 'time' : 'times'}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sentiment polls */}
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="community-polls-section">
        <h2 className="text-base sm:text-lg font-bold mb-3 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-violet-500 dark:text-violet-400" />
          Sentiment Polls
        </h2>
        {polls.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active polls right now. Polls auto-rotate around the most-held tickers in the community.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {polls.map((p) => (
              <PollCard key={p.poll_id} poll={p} canVote={!!me?.is_member} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneMap = {
    violet:  'bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300',
    amber:   'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300',
  };
  return (
    <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3" data-testid={`community-stat-${label.toLowerCase().replace(/\W+/g, '-')}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${toneMap[tone] || ''}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-muted-foreground">{label}</div>
        <div className="text-lg font-bold text-foreground">{value}</div>
      </div>
    </div>
  );
}

function PollCard({ poll, canVote }) {
  const [myChoice, setMyChoice] = useState(poll.my_choice || null);
  const [counts, setCounts] = useState(poll.counts || { buying: 0, holding: 0, trimming: 0, selling: 0 });
  const [total, setTotal] = useState(poll.total_votes || 0);
  const [submitting, setSubmitting] = useState(false);

  const handleVote = async (choice) => {
    if (!canVote || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/community/polls/${poll.poll_id}/vote`,
        { choice },
        { withCredentials: true },
      );
      setMyChoice(choice);
      setCounts(data.counts || counts);
      setTotal(data.total_votes || total);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not vote.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const opts = ['buying', 'holding', 'trimming', 'selling'];
  const optMeta = {
    buying:   { label: 'Buying',   color: 'emerald' },
    holding:  { label: 'Holding',  color: 'sky' },
    trimming: { label: 'Trimming', color: 'amber' },
    selling:  { label: 'Selling',  color: 'rose' },
  };

  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={`community-poll-${poll.ticker}`}>
      <div className="text-sm font-semibold mb-1">${poll.ticker}</div>
      <div className="text-xs text-muted-foreground mb-2">{poll.question}</div>
      <div className="space-y-1.5">
        {opts.map((o) => {
          const pct = total > 0 ? Math.round((100 * (counts[o] || 0)) / total) : 0;
          const isMine = myChoice === o;
          const meta = optMeta[o];
          return (
            <button
              key={o}
              type="button"
              onClick={() => handleVote(o)}
              disabled={!canVote || submitting}
              className={`relative w-full text-left rounded-md border px-2 py-1 text-[11px] font-medium transition-colors overflow-hidden ${
                isMine ? `border-${meta.color}-500 bg-${meta.color}-50 dark:bg-${meta.color}-950/40` : 'border-border bg-muted/20 hover:bg-muted/40'
              } ${!canVote ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid={`community-poll-vote-${poll.ticker}-${o}`}
            >
              <div className={`absolute inset-y-0 left-0 bg-${meta.color}-200/60 dark:bg-${meta.color}-900/40 transition-all`} style={{ width: `${pct}%` }} />
              <div className="relative flex items-center justify-between">
                <span>{isMine ? '✓ ' : ''}{meta.label}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5">{total} vote{total === 1 ? '' : 's'}</div>
    </div>
  );
}
